import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '@ohif/ui-next';
import { bytesToMegabytes, MAX_TOTAL_SIZE_BYTES } from '../../utils/dicomIntake';

interface DropZoneProps {
  isIntaking: boolean;
  onFiles: (files: File[]) => void;
}

/**
 * D12. `webkitdirectory` is not reachable through react-dropzone@10's
 * getInputProps, which is why develop destructured getInputProps and then ignored
 * it in favour of an imperative input (DicomUpload.tsx:73-91) — that reads like
 * ignorance of the API rather than a workaround, so the reason is written down.
 *
 * Plan D-16: develop wrapped each picker in its own <Dropzone noDrag> whose render
 * prop supplied the getInputProps it discarded, so both inner dropzones contributed
 * nothing but a div. Only the outer drag target needs react-dropzone at all.
 */
function openPicker(onFiles: (files: File[]) => void, directory: boolean): void {
  const input = document.createElement('input');

  input.type = 'file';
  input.multiple = true;

  if (directory) {
    input.webkitdirectory = true;
  }

  input.onchange = () => onFiles(Array.from(input.files ?? []));
  input.click();
}

export default function DropZone({ isIntaking, onFiles }: DropZoneProps) {
  const onDrop = useCallback((accepted: File[]) => onFiles(accepted), [onFiles]);

  // `accept` is deliberately absent. §5.3: most DICOM files carry no extension at
  // all, which is why develop's own isDicomFile() degenerated to `return true`
  // (DicomUploadProcessor.ts:20-25). It is also the v10-to-v14 API break the
  // parent's feature table asked about — inert, because nothing uses it (§1).
  const { getRootProps } = useDropzone({ onDrop, noClick: true, noKeyboard: true });

  return (
    <div
      {...getRootProps()}
      // M5.5 D10.2, retiring M5.4 D11's "legibility here is the weight, not the contrast".
      // develop drew this outline as four repeating-linear-gradients rather than a border
      // (develop:extensions/cornerstone/src/components/DicomUpload/DicomUpload.css), which is
      // why no border class ever traced to it: its #7BB2CE measures 7.95:1 on --background
      // where border-input measures 1.21:1 and vanishes. border-primary is 5.82:1; an alpha
      // of it would recompose the maroon wash the rest of this milestone is removing.
      className="border-primary relative m-5 flex h-full flex-col items-center justify-center rounded-2xl border-[3px] border-dashed"
      data-cy="sycai-upload-dropzone"
    >
      {/*
        Plan D-2, against design D4. The async path removes the FREEZE, not the need
        for feedback — and plan D-3 shows it does not even remove the freeze for a
        study of small instances, since fflate inflates anything under 320 000
        compressed bytes with inflateSync on the calling thread. `absolute inset-0`
        also masks the drop target, so a second drop cannot land mid-intake; the
        hook guards that in code as well, because a mask is presentation.
      */}
      {isIntaking && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50"
          data-cy="sycai-upload-intaking"
        >
          <div className="text-lg text-white">Extracting files...</div>
        </div>
      )}

      <div className="flex w-full flex-col items-center gap-6">
        <div className="flex gap-3">
          {/*
            M5.5 R-K. text-white on both pickers, not Button's own text-primary-foreground:
            the fork's ruled convention is white on the orange accent, and it is carried as a
            class here rather than by moving --primary-foreground, which also labels the green
            Submit in the two QA dialogs.
          */}
          <Button
            variant="default"
            size="lg"
            className="text-white"
            dataCY="sycai-upload-add-files"
            onClick={() => openPicker(onFiles, false)}
          >
            Add File
          </Button>
          {/*
            M5.5 D5. develop gave both pickers ButtonEnums.type.primary and distinguished
            the second with className="border-primary-main" alone -- a border COLOUR with no
            width beside it, which Tailwind's preflight zeroes, so the two rendered as one
            flat orange. The border half of the design's idiom is not replayed because
            develop never drew one; only the wash on this peer is the regression.
          */}
          <Button
            variant="default"
            size="lg"
            className="text-white"
            dataCY="sycai-upload-add-folder"
            onClick={() => openPicker(onFiles, true)}
          >
            Add Folder
          </Button>
        </div>

        <div className="text-foreground pt-5">or drag images or folders here</div>
        <div className="text-muted-foreground pt-3 text-lg">
          (DICOM files, ZIP archives supported. Max total size of unzipped study:{' '}
          {bytesToMegabytes(MAX_TOTAL_SIZE_BYTES)}MB
        </div>
      </div>
    </div>
  );
}
